import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-hot-toast';
import StarRating from './components/StarRating';
import { useTranslation } from 'react-i18next';
import { useAuth } from './AuthContext';
import { reviewService, orderService } from './services/api';

export default function MyOrders() {
  const { user } = useAuth();
  const [email, setEmail] = useState('');
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searched, setSearched] = useState(false);
  const [showReviewModal, setShowReviewModal] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [reviewFormData, setReviewFormData] = useState({
    rating: 0,
    review_title: '',
    review_text: ''
  });
  const [submitting, setSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState('');
  const navigate = useNavigate();
  const { t } = useTranslation();

  // Auto-fetch orders for logged-in users
  React.useEffect(() => {
    if (user?.email) {
      setEmail(user.email);
      fetchOrdersByEmail(user.email);
    }
  }, [user]);

  const fetchOrdersByEmail = async (emailAddress) => {
    if (!emailAddress) {
      toast.error('Please enter your email address');
      return;
    }

    setLoading(true);
    setSearched(true);

    try {
      const response = await orderService.getByEmail(emailAddress);
      setOrders(response.data);
    } catch (error) {
      console.error('Error fetching orders:', error);
      toast.error('Failed to fetch orders. Please try again.');
    } finally {
      setLoading(false);
    }
  };


  const fetchOrders = () => {
    fetchOrdersByEmail(email);
  };

  const openReviewModal = (product, orderId) => {
    setSelectedProduct({ ...product, orderId });
    setShowReviewModal(true);
    setReviewFormData({
      rating: 0,
      review_title: '',
      review_text: ''
    });
    setSubmitStatus('');
  };

  const closeReviewModal = () => {
    setShowReviewModal(false);
    setSelectedProduct(null);
    setReviewFormData({
      rating: 0,
      review_title: '',
      review_text: ''
    });
    setSubmitStatus('');
  };

  const handleSubmitReview = async (e) => {
    e.preventDefault();

    if (reviewFormData.rating === 0) {
      toast.error('Please select a rating');
      return;
    }

    setSubmitting(true);
    setSubmitStatus('');

    try {
      // Get customer name from the first order
      const customerName = orders[0]?.user_name || 'Customer';
      
      // Get product ID safely
      const productId = selectedProduct.productDetails?.id || selectedProduct.id;
      
      console.log('Review submission data:', {
        productId,
        customerName,
        email,
        selectedProduct
      });
      
      if (!productId) {
        console.error('Product ID missing from selectedProduct:', selectedProduct);
        toast.error('Unable to submit review: Product ID not found');
        setSubmitStatus('error');
        setSubmitting(false);
        return;
      }

      const response = await reviewService.create({
        product_id: productId,
        customer_name: customerName,
        customer_email: email,
        rating: reviewFormData.rating,
        review_title: reviewFormData.review_title,
        review_text: reviewFormData.review_text
      });

      const data = response.data;
      
      console.log('Review submission response:', { status: response.status, data });

      // Axios follows redirects and handles ok status automatically, 
      // but we need to check the custom status codes returned by the Hono worker if any.
      if (response.status === 200 || response.status === 201) {
        setSubmitStatus('success');
        setTimeout(() => {
          closeReviewModal();
          fetchOrders(); // Refresh orders to show updated review status
        }, 2000);
      } else {
        // Handle specific error cases from data if needed
        console.error('Review submission failed:', data);
        setSubmitStatus('error');
      }
    } catch (error) {
      console.error('Error submitting review:', error);
      if (error.response) {
        if (error.response.status === 403 && error.response.data.requiresPurchase) {
          setSubmitStatus('requiresPurchase');
        } else if (error.response.status === 409 && error.response.data.alreadyReviewed) {
          setSubmitStatus('alreadyReviewed');
        } else {
          setSubmitStatus('error');
        }
      } else {
        setSubmitStatus('error');
      }
    } finally {
      setSubmitting(false);
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const getStatusColor = (status) => {
    const statusLower = status?.toLowerCase() || '';
    if (statusLower === 'delivered') return 'text-green-600 dark:text-green-400 bg-green-50 dark:bg-green-900/20';
    if (statusLower === 'shipped') return 'text-blue-600 dark:text-blue-400 bg-blue-50 dark:bg-blue-900/20';
    return 'text-yellow-700 dark:text-yellow-500 bg-yellow-50 dark:bg-yellow-900/40';
  };

  return (
    <div className="min-h-screen bg-raya-bg py-12 px-4 sm:px-6 lg:px-8 transition-colors duration-300">
      <div>
        {/* Header */}
        <div className="text-center mb-12">
          <h1 className="text-4xl md:text-5xl font-serif font-bold text-raya-text-primary mb-4">
            {t('My Orders')}
          </h1>
          <p className="text-raya-text-secondary text-lg">
            View your purchase history and leave reviews
          </p>
        </div>

        {/* Email Search - Only for non-logged-in users */}
        {!user ? (
          <div className="bg-raya-card/70 backdrop-blur-md rounded-2xl shadow-xl p-8 mb-8 border border-raya-border">
            <label className="block text-sm font-semibold text-raya-text-secondary mb-3 uppercase tracking-wider">
              Enter your email to view orders
            </label>
            <div className="flex gap-4">
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && fetchOrders()}
                placeholder="your.email@example.com"
                className="flex-1 px-4 py-3 rounded-xl border border-raya-border bg-raya-bg/50 dark:bg-raya-card/50 text-raya-text-primary focus:ring-2 focus:ring-yellow-500/50 focus:border-transparent transition-all"
              />
              <button
                onClick={fetchOrders}
                disabled={loading}
                className="px-8 py-3 bg-gradient-to-r from-yellow-500 to-yellow-600 text-black rounded-xl font-bold hover:shadow-lg hover:scale-105 transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:scale-100"
              >
                {loading ? 'Loading...' : 'Search'}
              </button>
            </div>
          </div>
        ) : (
          <div className="bg-raya-accent-light/50 rounded-2xl shadow-sm p-6 mb-8 border border-yellow-200/50 dark:border-yellow-900/30">
            <p className="text-center text-lg text-raya-text-primary">
              Viewing orders for: <span className="font-bold text-yellow-600 dark:text-yellow-500">{user.email}</span>
            </p>
          </div>
        )}

        {/* Loading State */}
        {loading && (
          <div className="flex justify-center py-12">
            <div className="animate-spin rounded-full h-16 w-16 border-4 border-yellow-500 border-t-transparent"></div>
          </div>
        )}

        {/* Empty State */}
        {searched && !loading && orders.length === 0 && (
          <div className="bg-raya-card/70 backdrop-blur-md rounded-2xl shadow-xl p-12 text-center border border-raya-border">
            <div className="text-6xl mb-4">📦</div>
            <h3 className="text-2xl font-serif font-bold text-raya-text-primary mb-2">
              No Orders Found
            </h3>
            <p className="text-raya-text-secondary mb-6 font-medium">
              We couldn't find any orders for this email address.
            </p>
            <button
              onClick={() => navigate('/products')}
              className="px-8 py-3 bg-gradient-to-r from-yellow-500 to-yellow-600 text-black rounded-xl font-bold hover:shadow-lg hover:scale-105 transition-all duration-300"
            >
              Start Shopping
            </button>
          </div>
        )}

        {/* Orders List */}
        {!loading && orders.length > 0 && (
          <div className="space-y-6">
            {orders.map((order) => (
              <div
                key={order.id}
                className="bg-raya-card/80 backdrop-blur-md rounded-2xl shadow-xl overflow-hidden border border-raya-border hover:shadow-2xl transition-all duration-300"
              >
                {/* Order Header */}
                <div className="bg-raya-section/50 p-6 border-b border-raya-border">
                  <div className="flex flex-wrap items-center justify-between gap-4">
                    <div>
                      <h3 className="text-lg font-bold text-raya-text-primary">
                        Order #{String(order.id).substring(0, 8)}
                      </h3>
                      <p className="text-sm text-raya-text-tertiary mt-1">
                        Placed on {formatDate(order.created_at)}
                      </p>
                    </div>
                    <div className="flex items-center gap-4">
                      <span className={`px-4 py-2 rounded-full text-sm font-semibold capitalize ${getStatusColor(order.status)}`}>
                        {order.status || 'Pending'}
                      </span>
                      <div className="text-right">
                        <p className="text-sm text-raya-text-tertiary">Total</p>
                        <p className="text-xl font-bold text-raya-text-primary">
                          ${Number(order.total || 0).toFixed(2)}
                        </p>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Order Items */}
                <div className="p-6 space-y-4">
                  {order.items.map((item, index) => (
                    <div
                      key={index}
                      className="flex flex-col sm:flex-row gap-4 p-4 rounded-xl bg-raya-section/30 hover:bg-raya-section/50 transition-colors border border-raya-border"
                    >
                      {/* Product Image */}
                      <div className="flex-shrink-0">
                        <img
                          src={item.productDetails?.image || '/placeholder.png'}
                          alt={item.productDetails?.name || item.name}
                          className="w-24 h-24 object-cover rounded-lg"
                        />
                      </div>

                      {/* Product Details */}
                      <div className="flex-1">
                        <h4 className="font-semibold text-raya-text-primary text-lg mb-1">
                          {item.productDetails?.name || item.name}
                        </h4>
                        <p className="text-raya-text-secondary mb-2 font-medium">
                          Quantity: {item.quantity} × ${Number((item.productDetails?.price || item.price) || 0).toFixed(2)}
                        </p>
                        
                        {/* Review Status/Button */}
                        {item.hasReview ? (
                          <div className="flex items-center gap-2">
                            <div className="inline-flex items-center gap-2 px-4 py-2 bg-green-50 dark:bg-green-900/20 text-green-700 dark:text-green-300 rounded-lg">
                              <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                              </svg>
                              <span className="font-medium">Reviewed</span>
                            </div>
                            {item.review && (
                              <StarRating rating={item.review.rating} size="sm" />
                            )}
                          </div>
                        ) : order.status === 'delivered' ? (
                          <button
                            onClick={() => openReviewModal(item, order.id)}
                            className="inline-flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-yellow-500 to-yellow-600 text-black rounded-lg font-bold hover:shadow-lg hover:scale-105 transition-all duration-300"
                          >
                            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11.049 2.927c.3-.921 1.603-.921 1.902 0l1.519 4.674a1 1 0 00.95.69h4.915c.969 0 1.371 1.24.588 1.81l-3.976 2.888a1 1 0 00-.363 1.118l1.518 4.674c.3.922-.755 1.688-1.538 1.118l-3.976-2.888a1 1 0 00-1.176 0l-3.976 2.888c-.783.57-1.838-.197-1.538-1.118l1.518-4.674a1 1 0 00-.363-1.118l-3.976-2.888c-.784-.57-.38-1.81.588-1.81h4.914a1 1 0 00.951-.69l1.519-4.674z" />
                            </svg>
                            Leave Review
                          </button>
                        ) : (
                          <div className="inline-flex items-center gap-2 px-4 py-2 bg-raya-section text-raya-text-tertiary rounded-lg">
                            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                            </svg>
                            <span className="text-sm">Review after delivery</span>
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Review Modal */}
      {showReviewModal && selectedProduct && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-md flex items-center justify-center p-4 z-50">
          <div className="bg-raya-card/95 rounded-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto shadow-2xl border border-raya-border">
            <div className="p-8">
              {/* Modal Header */}
              <div className="flex items-center justify-between mb-6">
                <h3 className="text-2xl font-bold text-raya-text-primary">
                  Leave a Review
                </h3>
                <button
                  onClick={closeReviewModal}
                  className="text-raya-text-tertiary hover:text-raya-text-primary text-2xl"
                >
                  ✕
                </button>
              </div>

              {/* Product Info */}
              <div className="flex items-center gap-4 p-4 bg-raya-section rounded-xl mb-6 border border-raya-border">
                <img
                  src={selectedProduct.productDetails?.image}
                  alt={selectedProduct.productDetails?.name}
                  className="w-20 h-20 object-cover rounded-lg"
                />
                <div>
                  <h4 className="font-semibold text-raya-text-primary text-lg">
                    {selectedProduct.productDetails?.name}
                  </h4>
                  <p className="text-raya-text-secondary font-medium">
                    ${Number(selectedProduct.productDetails?.price || 0).toFixed(2)}
                  </p>
                </div>
              </div>

              {/* Review Form */}
              <form onSubmit={handleSubmitReview} className="space-y-6">
                <div>
                  <label className="block text-sm font-medium text-raya-text-primary mb-2">
                    Your Rating *
                  </label>
                  <StarRating
                    rating={reviewFormData.rating}
                    size="xl"
                    interactive={true}
                    onRatingChange={(rating) => setReviewFormData({ ...reviewFormData, rating })}
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-raya-text-primary mb-2">
                    Review Title
                  </label>
                  <input
                    type="text"
                    value={reviewFormData.review_title}
                    onChange={(e) => setReviewFormData({ ...reviewFormData, review_title: e.target.value })}
                    className="w-full px-4 py-3 rounded-xl border border-raya-border bg-raya-bg/50 dark:bg-raya-card/50 text-raya-text-primary focus:ring-2 focus:ring-yellow-500/50 focus:border-transparent transition-all"
                    placeholder="Summarize your experience"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-raya-text-primary mb-2">
                    Your Review *
                  </label>
                  <textarea
                    required
                    rows={5}
                    value={reviewFormData.review_text}
                    onChange={(e) => setReviewFormData({ ...reviewFormData, review_text: e.target.value })}
                    className="w-full px-4 py-3 rounded-xl border border-raya-border bg-raya-bg/50 dark:bg-raya-card/50 text-raya-text-primary focus:ring-2 focus:ring-yellow-500/50 focus:border-transparent transition-all resize-none"
                    placeholder="Share your experience with this product..."
                  />
                </div>

                <div className="flex gap-4">
                  <button
                    type="submit"
                    disabled={submitting}
                    className="flex-1 px-6 py-3 bg-gradient-to-r from-yellow-500 to-yellow-600 text-black rounded-xl font-bold hover:shadow-lg hover:scale-105 transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:scale-100"
                  >
                    {submitting ? 'Submitting...' : 'Submit Review'}
                  </button>
                  <button
                    type="button"
                    onClick={closeReviewModal}
                    className="px-6 py-3 bg-raya-section text-raya-text-primary rounded-xl font-bold hover:bg-raya-border transition-all duration-300"
                  >
                    Cancel
                  </button>
                </div>

                {/* Status Messages */}
                {submitStatus === 'success' && (
                  <div className="p-4 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-xl text-green-800 dark:text-green-200">
                    ✓ Review submitted successfully!
                  </div>
                )}

                {submitStatus === 'requiresPurchase' && (
                  <div className="p-4 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-xl text-amber-800 dark:text-amber-200">
                    ⚠ You can only review products you've purchased.
                  </div>
                )}

                {submitStatus === 'alreadyReviewed' && (
                  <div className="p-4 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-xl text-blue-800 dark:text-blue-200">
                    ℹ You've already reviewed this product.
                  </div>
                )}

                {submitStatus === 'error' && (
                  <div className="p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-xl text-red-800 dark:text-red-200">
                    Failed to submit review. Please try again.
                  </div>
                )}
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
